import * as THREE from 'three'
import {
  types,
  getParent,
  Instance,
  tryResolve,
  SnapshotIn,
  detach
} from 'mobx-state-tree'
import { ElementEdge } from 'src/api/models/Elements/ElementEdge'
import { IElementEdge } from 'src/api/models/Elements/ElementEdge/types'
import { generateType } from 'src/api/models/utils/generateNodeType'
import {
  elementConnection,
  coverPlate,
  elementEdge
} from 'src/constants/typenames'
import { IElementConnection } from './types'
import { IPrintedcircuitboard } from '../Content/Printedcircuitboard/types'
import { ICoverPlate } from '../Enclosure/Cover/types'
import { IAdapterPlate } from '../Enclosure/Adapter/types'
import { IProject } from '../Project/types'

type ElementEdgeInstance = Instance<IElementEdge>

const ElementConnection = (): IElementConnection =>
  generateType(elementConnection)
    .props({
      edges: types.array(types.late(ElementEdge))
    })
    .actions((self) => ({
      addElement(
        input:
          | ElementEdgeInstance['node']['__typename']
          | SnapshotIn<ElementEdgeInstance>,
        persist: boolean = true
      ) {
        const newElementEdge =
          typeof input === 'string' ? { node: { __typename: input } } : input
        self.edges.push(newElementEdge)
        return self.edges[self.edges.length - 1]
      },
      removeElement(id: string, persist: boolean = true) {
        const index = self.edges.findIndex((element) => element.id === id)
        const referenceOf = self.edges.filter(
          (edge) => edge.reference.value?.id === id
        )
        if (referenceOf.length > 0) {
          const plate = getParent<Instance<ICoverPlate>>(self)
          referenceOf.forEach((elementEdge) =>
            elementEdge.reference.update(plate.id)
          )
        }
        self.edges.splice(index, 1)
      },
      detachElement(id: string, persist: boolean = false) {
        const index = self.edges.findIndex((element) => element.id === id)
        detach(self.edges[index])
      }
      // reassignElement(
      //   { currentIndex, nextIndex, item },
      //   persist: boolean = true
      // ) {
      //   if (currentIndex !== nextIndex) {
      //     const first = self.edges.slice(0, currentIndex)
      //     const last = self.edges.slice(currentIndex + 1)
      //     const nextEdges = [...first, ...last]
      //     nextEdges.splice(nextIndex, 0, item)
      //     self.edges = nextEdges
      //   }
      // }
    }))
    .views((self) => ({
      /*
       * clipping area should be aware of adapterheads
       *
       */
      get clippingArea() {
        return new THREE.Box3()
      },
      /*
       * engraving area should be aware of srew holes
       *
       */
      get engravingArea() {
        return new THREE.Box3()
      },
      get clippingEdges() {
        return self.edges.filter((edge) =>
          edge.node.__typename.includes('Clipping')
        )
      },
      get engravingEdges() {
        return self.edges.filter((edge) =>
          edge.node.__typename.includes('Engraving')
        )
      },
      getReferenceOptions(exclude?: string | string[]) {
        const parent = getParent<
          Instance<IPrintedcircuitboard | ICoverPlate | IAdapterPlate>
        >(self, 1)
        const result = [parent, ...self.edges]
        switch (parent.__typename) {
          case coverPlate:
            //9. Project 8. EnclosureConnection 7. types.array 6. CoverEdge 5. Cover 4. CoverPlateConnection 3. types.array 2. CoverPlateEdge 1. CoverPlate
            const project = getParent<Instance<IProject>>(self, 9)
            const pcb = tryResolve(project, '/content/edges/0/node')
            !!pcb && result.push(pcb)
        }
        // filter elements that are referenced by this id
        if (exclude)
          return result.filter((component) => deepFilter(exclude, component))
        return result
      }
    }))

const deepFilter = (
  id: string | string[],
  element: ElementEdgeInstance | { id: string; reference?: $FixMe }
): boolean => {
  if (Array.isArray(id) && id.includes(element.id)) return false
  if (id === element.id) return false
  if (element.reference?.value.__typename === elementEdge) {
    return deepFilter(id, element.reference.value)
  }
  return true
}

export { ElementConnection, ElementEdge }

export {
  CircularClippingElement,
  Element,
  CircularEngravingElement,
  RectangularClippingElement,
  RectangularEngravingElement
} from 'src/api/models/Elements/Element'
export { TextEngravingElement } from 'src/api/models/Elements/Element/TextEngravingElement'
